import { useEffect, useState } from 'react';
import type { Movie } from '@/utils/movies';

interface PlayerModalProps {
  movie: Movie | null;
  open: boolean;
  onClose: () => void;
}

export default function PlayerModal({ movie, open, onClose }: PlayerModalProps) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 1500);
    return () => window.clearTimeout(timer);
  }, [open, movie?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || !movie) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-primary-950/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${movie.title} 演示播放器`}
    >
      <div
        className="w-full max-w-4xl bg-primary-950 rounded-lg overflow-hidden border border-primary-700 animate-fade-up"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-primary-800">
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-play-circle-line text-accent-500 text-xl w-5 h-5 flex items-center justify-center"></i>
            <h3 className="font-heading font-bold text-sm text-background-50 truncate">
              正在播放：{movie.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭播放器"
            className="w-8 h-8 flex items-center justify-center rounded-md text-background-200 hover:bg-primary-800 cursor-pointer"
          >
            <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
          </button>
        </div>

        <div className="relative aspect-video bg-black">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 播放画面示意`}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          {loading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <span className="w-12 h-12 rounded-full border-[3px] border-primary-700 border-t-accent-500 animate-spin-slow"></span>
              <p className="text-background-200 text-sm font-label animate-pulse-soft">正在缓冲演示画面…</p>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6">
              <span className="w-16 h-16 rounded-full bg-accent-500 flex items-center justify-center">
                <i className="ri-play-fill text-primary-950 text-3xl w-8 h-8 flex items-center justify-center"></i>
              </span>
              <p className="text-background-50 text-base md:text-lg font-heading font-bold">
                演示效果，待接入真实播放接口
              </p>
              <p className="text-background-300 text-sm max-w-md leading-relaxed">
                当前页面仅为播放器界面示范，未接入任何真实视频流，无法实际播放影片。
              </p>
            </div>
          )}
          <div className="absolute left-0 right-0 bottom-0 px-4 py-3 bg-gradient-to-t from-black/85 to-transparent">
            <div className="h-1 w-full rounded-full bg-background-50/25 overflow-hidden">
              <div className={`h-full bg-accent-500 rounded-full transition-all duration-1000 ${loading ? 'w-1/4' : 'w-1/2'}`}></div>
            </div>
            <div className="mt-2 flex items-center justify-between text-background-100 text-xs">
              <div className="flex items-center gap-3">
                <i className="ri-play-fill w-4 h-4 flex items-center justify-center"></i>
                <i className="ri-volume-up-line w-4 h-4 flex items-center justify-center"></i>
                <span className="font-label">00:00 / {Math.floor(movie.duration / 60)}:{String(movie.duration % 60).padStart(2, '0')}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2 py-0.5 rounded bg-background-50/15 font-label">演示</span>
                <i className="ri-fullscreen-line w-4 h-4 flex items-center justify-center"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}